import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Icon, Switch, InputNumber, Input, Button, Modal, Skeleton, Tag, Spin } from 'antd';
import styled from 'styled-components';

import { convertToBRL } from '~/utils/formatters';

var LEVELS = [
  { key: '1', color: '#faad14', title: '1 mensalidade — aviso no topo do painel' },
  { key: '2', color: '#fa8c16', title: '2 mensalidades — o cliente precisa clicar em Li e estou ciente' },
  { key: '3', color: '#f5222d', title: '3 ou mais — painel bloqueado até falar com o financeiro' },
];

var PREVIEW_ITEMS = [
  { key: '1', label: 'Nível 1', hint: 'aviso no topo', color: '#faad14' },
  { key: '2', label: 'Nível 2', hint: 'ciência obrigatória', color: '#fa8c16' },
  { key: '3', label: 'Nível 3', hint: 'painel bloqueado', color: '#f5222d' },
  { key: 'released', label: 'Liberados', hint: 'acordo em vigor', color: '#52c41a' },
];

export var SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

var Wrapper = styled.div`
  margin-top: 32px;
`;

var Panel = styled.div`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  padding: 20px 24px;
`;

var Block = styled.div`
  padding: 16px 0;
  border-top: 1px solid #f5f5f5;

  &:first-child {
    border-top: none;
    padding-top: 0;
  }
`;

var BlockTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 4px;
`;

var Help = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.5;
  margin-top: 4px;
`;

var FieldsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

var FieldLabel = styled.div`
  font-size: 13px;
  color: #595959;
  margin-bottom: 6px;
`;

var LevelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

var LevelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #262626;
`;

var Dot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${function(p) { return p.color; }};
  flex-shrink: 0;
`;

var LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  color: ${function(p) { return p.disabled ? '#bfbfbf' : '#1890ff'; }};
  cursor: ${function(p) { return p.disabled ? 'default' : 'pointer'; }};
  white-space: nowrap;
`;

var StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

var Stat = styled.div`
  border: 1px solid ${function(p) { return p.active ? p.color : '#f0f0f0'; }};
  background: ${function(p) { return p.active ? '#fafafa' : '#fff'; }};
  border-radius: 8px;
  padding: 10px 12px;
  cursor: ${function(p) { return p.clickable ? 'pointer' : 'default'; }};
  transition: border-color 0.2s;

  &:hover {
    border-color: ${function(p) { return p.clickable ? p.color : '#f0f0f0'; }};
  }
`;

var StatValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${function(p) { return p.color; }};
  line-height: 1.2;
`;

var StatLabel = styled.div`
  font-size: 12px;
  color: #595959;
`;

var List = styled.div`
  margin-top: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  max-height: 260px;
  overflow-y: auto;
`;

var ListRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 6px 12px;
  font-size: 12px;
  color: #595959;
  border-top: 1px solid #f5f5f5;

  &:first-child {
    border-top: none;
  }
`;

var Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

var onlyDigits = function(value) {
  return String(value || '').replace(/\D/g, '');
};

var pluralize = function(n, singular, plural) {
  return n + ' ' + (n === 1 ? singular : plural);
};

var countOf = function(overview, key) {
  if (!overview) return 0;
  if (key === 'released') return (overview.released || []).length;
  return ((overview.levels && overview.levels[key]) || []).length;
};

var rowsOf = function(overview, key) {
  if (!overview) return [];
  if (key === 'released') return overview.released || [];
  return (overview.levels && overview.levels[key]) || [];
};

var DefaulterNotices = function() {
  var dispatch = useDispatch();

  var loadingState = useState(true);
  var isLoading = loadingState[0];
  var setIsLoading = loadingState[1];

  var savingState = useState(false);
  var isSaving = savingState[0];
  var setIsSaving = savingState[1];

  var formState = useState(null);
  var form = formState[0];
  var setForm = formState[1];

  var savedEnabledState = useState(false);
  var savedEnabled = savedEnabledState[0];
  var setSavedEnabled = savedEnabledState[1];

  var defaultsState = useState(null);
  var defaults = defaultsState[0];
  var setDefaults = defaultsState[1];

  var fallbackState = useState(null);
  var fallbackWhatsapp = fallbackState[0];
  var setFallbackWhatsapp = fallbackState[1];

  var overviewState = useState(null);
  var overview = overviewState[0];
  var setOverview = overviewState[1];

  var overviewLoadingState = useState(false);
  var isOverviewLoading = overviewLoadingState[0];
  var setIsOverviewLoading = overviewLoadingState[1];

  var expandedState = useState(null);
  var expanded = expandedState[0];
  var setExpanded = expandedState[1];

  var requestIdRef = useRef(0);

  var fetchOverview = useCallback(function(graceDays) {
    requestIdRef.current += 1;
    var requestId = requestIdRef.current;
    setIsOverviewLoading(true);
    return dispatch({
      type: 'DEFAULTER_NOTICES_OVERVIEW',
      payload: {
        request: {
          method: 'get',
          url: '/defaulter-notices/overview?grace_days=' + graceDays,
          showLoading: false,
        },
      },
    }).then(function(response) {
      var data = response.payload.data.data;
      if (requestId === requestIdRef.current) {
        setOverview(data);
        setIsOverviewLoading(false);
      }
      return data;
    }).catch(function() {
      if (requestId === requestIdRef.current) {
        setIsOverviewLoading(false);
      }
      return null;
    });
  }, [dispatch]);

  useEffect(function() {
    dispatch({
      type: 'DEFAULTER_NOTICES_SETTINGS',
      payload: {
        request: {
          method: 'get',
          url: '/defaulter-notices/settings',
          showLoading: false,
        },
      },
    }).then(function(response) {
      var data = response.payload.data.data;
      var settings = data.settings || {};
      setDefaults(data.defaults || null);
      setFallbackWhatsapp(data.fallback_whatsapp || null);
      setSavedEnabled(!!settings.enabled);
      setForm({
        enabled: !!settings.enabled,
        grace_days: typeof settings.grace_days === 'number' ? settings.grace_days : 5,
        whatsapp: settings.whatsapp || '',
        messages: Object.assign({ '1': '', '2': '', '3': '' }, settings.messages || {}),
      });
      setIsLoading(false);
    }).catch(function() {
      setIsLoading(false);
    });
  }, [dispatch]);

  // Recalcula a prévia quando a tolerância muda (com atraso para não disparar a cada tecla)
  var graceDays = form ? form.grace_days : null;
  useEffect(function() {
    if (graceDays === null || graceDays === undefined || graceDays === '') return undefined;
    var timer = setTimeout(function() {
      fetchOverview(graceDays);
    }, 500);
    return function() { clearTimeout(timer); };
  }, [graceDays, fetchOverview]);

  var setField = function(field, value) {
    setForm(function(prev) {
      var next = Object.assign({}, prev);
      next[field] = value;
      return next;
    });
  };

  var setMessage = function(level, value) {
    setForm(function(prev) {
      var messages = Object.assign({}, prev.messages);
      messages[level] = value;
      return Object.assign({}, prev, { messages: messages });
    });
  };

  var restoreMessage = function(level) {
    if (!defaults || !defaults.messages) return;
    setMessage(level, defaults.messages[level] || '');
  };

  var doSave = function() {
    setIsSaving(true);
    var whatsapp = onlyDigits(form.whatsapp);
    return dispatch({
      type: 'DEFAULTER_NOTICES_SAVE',
      payload: {
        request: {
          method: 'put',
          url: '/defaulter-notices/settings',
          data: {
            enabled: !!form.enabled,
            grace_days: form.grace_days,
            whatsapp: whatsapp || null,
            messages: form.messages,
          },
        },
      },
    }).then(function(response) {
      var saved = response.payload.data.data || {};
      setSavedEnabled(!!saved.enabled);
      setForm(function(prev) {
        return Object.assign({}, prev, {
          enabled: !!saved.enabled,
          whatsapp: saved.whatsapp || '',
        });
      });
      setIsSaving(false);
    }).catch(function() {
      setIsSaving(false);
    });
  };

  var handleSave = function() {
    if (!form || isSaving) return;

    var turningOn = form.enabled && !savedEnabled;
    if (!turningOn) {
      doSave();
      return;
    }

    setIsSaving(true);
    fetchOverview(form.grace_days).then(function(data) {
      setIsSaving(false);
      var blocked = countOf(data, '3');
      var acks = countOf(data, '2');
      var banners = countOf(data, '1');
      Modal.confirm({
        title: 'Ligar os avisos agora?',
        content: React.createElement('div', null,
          React.createElement('p', { style: { marginBottom: 8 } },
            blocked > 0
              ? React.createElement('span', null,
                  React.createElement('strong', null, pluralize(blocked, 'cliente fica', 'clientes ficam')),
                  ' com o painel bloqueado agora.'
                )
              : 'Nenhum cliente fica com o painel bloqueado agora.'
          ),
          React.createElement('p', { style: { marginBottom: 0, color: '#8c8c8c', fontSize: 13 } },
            pluralize(acks, 'cliente precisa', 'clientes precisam') + ' confirmar ciência e ' +
            pluralize(banners, 'cliente vê', 'clientes veem') + ' o aviso no topo.'
          )
        ),
        okText: 'Ligar avisos',
        okType: blocked > 0 ? 'danger' : 'primary',
        cancelText: 'Cancelar',
        // a tela de configurações abre num drawer com zIndex 1010
        zIndex: 1100,
        onOk: function() { return doSave(); },
      });
    });
  };

  if (isLoading) {
    return React.createElement(Wrapper, null,
      React.createElement(SectionLabel, null,
        React.createElement(Icon, { type: 'notification', style: { fontSize: 14 } }),
        'Avisos de inadimplência'
      ),
      React.createElement(Skeleton, { active: true, paragraph: { rows: 4 } })
    );
  }

  if (!form) return null;

  var expandedRows = expanded ? rowsOf(overview, expanded) : [];

  return React.createElement(Wrapper, null,
    React.createElement(SectionLabel, null,
      React.createElement(Icon, { type: 'notification', style: { fontSize: 14 } }),
      'Avisos de inadimplência'
    ),
    React.createElement(Panel, null,

      // Liga/desliga
      React.createElement(Block, null,
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement(Switch, {
            checked: !!form.enabled,
            onChange: function(checked) { setField('enabled', checked); },
          }),
          React.createElement('div', null,
            React.createElement(BlockTitle, { style: { marginBottom: 0 } }, 'Avisar clientes inadimplentes no painel'),
            React.createElement(Help, { style: { marginTop: 0 } },
              'O cliente vê o aviso ao entrar no painel. Conta só mensalidades vencidas e não pagas.'
            )
          )
        )
      ),

      // Tolerância + WhatsApp
      React.createElement(Block, null,
        React.createElement(FieldsRow, null,
          React.createElement('div', null,
            React.createElement(FieldLabel, null, 'Tolerância'),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
              React.createElement(InputNumber, {
                min: 0,
                max: 90,
                precision: 0,
                value: form.grace_days,
                onChange: function(value) {
                  var n = parseInt(value, 10);
                  if (isNaN(n)) return;
                  setField('grace_days', Math.max(0, Math.min(90, n)));
                },
                style: { width: 90 },
              }),
              React.createElement('span', { style: { color: '#595959' } }, 'dias')
            ),
            React.createElement(Help, null,
              'Mensalidade conta como em aberto ' + pluralize(form.grace_days, 'dia', 'dias') + ' depois do vencimento'
            )
          ),
          React.createElement('div', null,
            React.createElement(FieldLabel, null, 'WhatsApp do financeiro'),
            React.createElement(Input, {
              value: form.whatsapp,
              placeholder: fallbackWhatsapp || 'Ex.: 11999999999',
              maxLength: 20,
              prefix: React.createElement(Icon, { type: 'message', style: { color: '#bfbfbf' } }),
              onChange: function(e) { setField('whatsapp', e.target.value); },
            }),
            React.createElement(Help, null, 'Vazio: usa o WhatsApp do Administrativo')
          )
        )
      ),

      // Mensagens
      React.createElement(Block, null,
        React.createElement(BlockTitle, null, 'Mensagens'),
        React.createElement(Help, { style: { marginTop: 0, marginBottom: 12 } },
          'Você pode usar ',
          React.createElement('code', null, '{quantidade}'), ', ',
          React.createElement('code', null, '{valor}'), ' e ',
          React.createElement('code', null, '{desde}'),
          ' — o painel troca pelos dados do cliente.'
        ),
        LEVELS.map(function(level) {
          var defaultText = defaults && defaults.messages ? defaults.messages[level.key] : '';
          var isDefault = !defaultText || form.messages[level.key] === defaultText;
          return React.createElement('div', { key: level.key, style: { marginBottom: 16 } },
            React.createElement(LevelHeader, null,
              React.createElement(LevelTitle, null,
                React.createElement(Dot, { color: level.color }),
                level.title
              ),
              React.createElement(LinkButton, {
                type: 'button',
                disabled: isDefault,
                onClick: function() { if (!isDefault) restoreMessage(level.key); },
              }, 'Restaurar texto padrão')
            ),
            React.createElement(Input.TextArea, {
              value: form.messages[level.key],
              maxLength: 500,
              autoSize: { minRows: 2, maxRows: 5 },
              onChange: function(e) { setMessage(level.key, e.target.value); },
            })
          );
        })
      ),

      // Prévia
      React.createElement(Block, null,
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 } },
          React.createElement(BlockTitle, { style: { marginBottom: 0 } }, 'Quem recebe hoje'),
          isOverviewLoading && React.createElement(Spin, { size: 'small' })
        ),
        React.createElement(Help, { style: { marginTop: 0, marginBottom: 12 } },
          'Clientes ativos, com tolerância de ' + pluralize(form.grace_days, 'dia', 'dias') + '. Clique num número para ver a lista.'
        ),
        React.createElement(StatsRow, null,
          PREVIEW_ITEMS.map(function(item) {
            var count = countOf(overview, item.key);
            var clickable = count > 0;
            return React.createElement(Stat, {
              key: item.key,
              color: item.color,
              active: expanded === item.key,
              clickable: clickable,
              onClick: function() {
                if (!clickable) return;
                setExpanded(expanded === item.key ? null : item.key);
              },
            },
              React.createElement(StatValue, { color: count > 0 ? item.color : '#bfbfbf' }, overview ? count : '–'),
              React.createElement(StatLabel, null, item.label + ' · ' + item.hint)
            );
          })
        ),
        expanded && expandedRows.length > 0 && React.createElement(List, null,
          expandedRows.map(function(row) {
            return React.createElement(ListRow, { key: row.customer_id },
              React.createElement('span', { style: { width: 56, color: '#8c8c8c', flexShrink: 0 } }, row.cod || '—'),
              React.createElement('span', { style: { flex: 1, color: '#262626', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, row.name),
              React.createElement('span', { style: { flexShrink: 0 } }, pluralize(row.overdue_count, 'mensalidade', 'mensalidades')),
              React.createElement('span', { style: { width: 100, textAlign: 'right', flexShrink: 0 } }, convertToBRL(row.overdue_total || 0))
            );
          })
        )
      ),

      React.createElement(Footer, null,
        !form.enabled && savedEnabled && React.createElement(Tag, { color: 'orange', style: { alignSelf: 'center', marginRight: 12 } },
          'Ao salvar, os avisos param de aparecer'
        ),
        React.createElement(Button, {
          type: 'primary',
          icon: 'save',
          loading: isSaving,
          onClick: handleSave,
        }, 'Salvar')
      )
    )
  );
};

export default DefaulterNotices;
